/**
 * Raketech Final Reconciliation
 * Combined view: recon-period data (Mar 2023 – Nov 2024) from Google Sheets
 *              + post-recon data (Dec 2024+) from Affiliation Cloud
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Scale,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  Database,
  FileText,
  RefreshCw,
  Link2,
  Unlink,
  ArrowUpRight,
  ArrowDownRight,
  Shield,
  Eye,
  EyeOff,
  Key,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types (recon-period from /api/raketech/compare)
// ---------------------------------------------------------------------------
interface ComparisonIssue {
  type: string;
  severity: string;
  accountId: string;
  username: string;
  brand: string;
  partner: string;
  offer: string;
  geo: string;
  currency: string;
  details: string;
  partnerGeoKey: string;

  invoiceFTDs?: number;
  invoiceRevenue?: number;
  reconciliationFTDs?: number;
  reconciliationEarningsOriginal?: number;
  reconciliationEarnings?: number;
  reconciliationRows?: number;
  partnerFTDs?: number;
  partnerCommissionOriginal?: number;
  partnerCommission?: number;
  partnerCommissionOurShare?: number;
  partnerSource?: string;

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsAccountName?: string;
  adwstatsFTDs?: number;
  adwstatsMatchMethod?: string;
  adwstatsDataSynced?: boolean;
  adwstatsPlatform?: string;

  manualCheckAmount?: number;
  manualCheckComment?: string;
  manualCheckSource?: string;

  ftdDifference?: number;
  revenueDifference?: number;
  percentageDifference?: number;
  exchangeRate?: number;

  missingInInvoice?: boolean;
  missingInReconciliation?: boolean;
  missingInPartner?: boolean;

  invoiceBrandKey?: string;
}

interface CompareData {
  period: string;
  exchangeRates: Record<string, number>;
  summary: {
    totalAccounts: number;
    matched: number;
    critical: number;
    underpaid: number;
    overpaid: number;
    missingInInvoice: number;
    missingInRecon: number;
    missingInPartner: number;
    noAccountsTracked: number;
  };
  totals: {
    invoiced: number;
    actualInvoiced: number;
    actualInvoiceCount: number;
    shouldBe: number;
    reconEarnings: number;
    partnerOurShare: number;
    adwstatsSynced: number;
    manualCheck: number;
    positiveCorrections: number;
    negativeCorrections: number;
  };
  adwstatsMatching: {
    matched: number;
    unmatched: number;
    matchedNotSynced: number;
    unmatchedAccounts: { accountId: string; username: string; partner: string; geo: string }[];
    totalLocalAccounts: number;
  };
  groups: ComparisonGroup[];
  issues: ComparisonIssue[];
}

interface ComparisonGroup {
  partner: string;
  geo: string;
  issues: ComparisonIssue[];
  totalInvoiced: number;
  shouldBe: number;
  underpaid: number;
  totalSynced: number;
}

// ---------------------------------------------------------------------------
// Types (post-recon from /api/raketech/post-recon)
// ---------------------------------------------------------------------------
interface PostReconRow {
  reconAccount: {
    accountId: string;
    username: string;
    partner: string;
    brand: string;
    offer: string;
    geo: string;
    currency: string;
    sources: string[];
    reconEarnings: number;
    invoiceRevenue: number;
    partnerCommission: number;
    manualCheckAmount: number;
  };
  acMatch: {
    acId: number;
    name: string;
    advertiser: string;
    system: string;
    status: string;
    brands: string[];
  } | null;
  matchMethod: string;
  localAccountId?: string;
  postReconSynced: number;
  postReconSyncedRaw?: number;
  postReconCurrency?: string;
  acReportCommission: number;
  revenueDiff: number;
}

interface PostReconData {
  period: { recon: string; postRecon: string };
  summary: {
    totalReconAccounts: number;
    totalAcAccounts: number;
    matched: number;
    missing: number;
    extraAcAccounts: number;
    withPostReconRevenue: number;
    withDiscrepancy: number;
    matchMethods: Record<string, number>;
  };
  rows: PostReconRow[];
  extraAcAccounts: {
    acId: number;
    name: string;
    advertiser: string;
    system: string;
    status: string;
    brands: string[];
    acReportCommission: number;
  }[];
}

// ---------------------------------------------------------------------------
// Merged issue type — extends recon issue with post-recon AC data
// ---------------------------------------------------------------------------
interface FinalIssue extends ComparisonIssue {
  acPostReconCommission?: number;
  acPostReconSynced?: number;
  acMatchMethod?: string;
  acMatched?: boolean;
  totalAllPeriods?: number;
}

interface FinalGroup {
  partner: string;
  geo: string;
  issues: FinalIssue[];
  totalInvoiced: number;
  shouldBe: number;
  underpaid: number;
  totalSynced: number;
  totalACPostRecon: number;
  totalAllPeriods: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

const getACToken = (): string | null => {
  try {
    const stored = localStorage.getItem('affiliation-cloud-token');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.expiresAt && Date.now() < parsed.expiresAt) return parsed.token;
    }
  } catch { /* ignore */ }
  return null;
};

const saveACToken = (token: string) => {
  localStorage.setItem('affiliation-cloud-token', JSON.stringify({
    token,
    expiresAt: Date.now() + 23 * 60 * 60 * 1000,
  }));
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtOriginalCurrency = (v: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

type TabFilter = 'all' | 'critical' | 'underpaid' | 'overpaid' | 'missing_invoice' | 'missing_recon' | 'missing_partner' | 'matches' | 'ac_only';

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechFinalRecon: React.FC = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [postReconData, setPostReconData] = useState<PostReconData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // AC token state
  const [acToken, setAcToken] = useState<string | null>(getACToken);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [acLoading, setAcLoading] = useState(false);

  const isACConnected = !!acToken;

  // -----------------------------------------------------------------------
  // Import data from Google Sheets
  // -----------------------------------------------------------------------
  const handleImport = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    setIsImporting(true);
    try {
      // Phased import — each phase is a separate request with its own time
      // budget so the whole job can't exceed a single Worker request limit.
      const phases = ['invoices', 'reconciliation', 'partner', 'extras'] as const;
      const summary: Record<string, any> = {};
      for (const phase of phases) {
        toast.info(`Importing: ${phase}…`);
        const resp = await fetch(`/api/raketech/import?phase=${phase}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await resp.json() as any;
        if (!json.success) throw new Error(`${phase}: ${json.error || json.message || 'Import failed'}`);
        Object.assign(summary, json.data.summary);
      }
      toast.success(`Import complete: ${summary.reconciliation || 0} recon, ${summary.invoices || 0} invoices`);
      fetchAllData();
    } catch (err: any) {
      toast.error(err.message || 'Import failed');
    } finally {
      setIsImporting(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Fetch both datasets
  // -----------------------------------------------------------------------
  const fetchAllData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    setIsLoading(true);
    setError(null);
    try {
      // Always fetch recon data
      const compareResp = await fetch('/api/raketech/compare', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const compareJson = await compareResp.json() as any;
      if (!compareJson.success) throw new Error(compareJson.error || compareJson.message || 'Comparison failed');
      if (compareJson.data.empty) {
        setError(compareJson.data.message);
        setCompareData(null);
        setPostReconData(null);
        return;
      }
      setCompareData(compareJson.data);

      // Fetch post-recon if AC token available
      const currentAcToken = acToken || getACToken();
      if (currentAcToken) {
        try {
          setAcLoading(true);
          const postReconResp = await fetch('/api/raketech/post-recon', {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Affiliation-Cloud-Token': currentAcToken,
            },
          });
          const postReconJson = await postReconResp.json() as any;
          if (postReconJson.success) {
            setPostReconData(postReconJson.data);
          } else {
            console.warn('Post-recon fetch failed:', postReconJson.error);
            setPostReconData(null);
          }
        } catch (err: any) {
          console.warn('Post-recon fetch error:', err.message);
          setPostReconData(null);
        } finally {
          setAcLoading(false);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [acToken]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  // -----------------------------------------------------------------------
  // AC token handlers
  // -----------------------------------------------------------------------
  const handleSaveToken = () => {
    if (!tokenInput.trim()) return;
    saveACToken(tokenInput.trim());
    setAcToken(tokenInput.trim());
    setTokenInput('');
    setShowTokenInput(false);
    toast.success('AC token saved');
    setTimeout(() => fetchAllData(), 100);
  };

  const handleDisconnect = () => {
    localStorage.removeItem('affiliation-cloud-token');
    setAcToken(null);
    setPostReconData(null);
    toast.info('Disconnected from AC');
  };

  // -----------------------------------------------------------------------
  // Merge recon + post-recon data into unified issues
  // -----------------------------------------------------------------------
  const mergedIssues = useMemo((): FinalIssue[] => {
    if (!compareData) return [];

    const acByAccountId = new Map<string, PostReconRow>();
    if (postReconData) {
      for (const row of postReconData.rows) {
        acByAccountId.set(row.reconAccount.accountId, row);
      }
    }

    const merged: FinalIssue[] = compareData.issues.map(issue => {
      const postRow = acByAccountId.get(issue.accountId);
      const acPostRecon = postRow?.acReportCommission || 0;
      const acSynced = postRow?.postReconSynced || 0;
      const reconBest = Math.max(
        issue.reconciliationEarnings || 0,
        issue.partnerCommissionOurShare || 0,
        (issue.adwstatsDataSynced !== false && issue.adwstatsCommission) ? issue.adwstatsCommission : 0,
        issue.manualCheckAmount || 0,
      );
      const totalAll = reconBest + acPostRecon;

      return {
        ...issue,
        acPostReconCommission: acPostRecon > 0 ? acPostRecon : undefined,
        acPostReconSynced: acSynced > 0 ? acSynced : undefined,
        acMatchMethod: postRow?.matchMethod,
        acMatched: postRow?.acMatch != null,
        totalAllPeriods: totalAll > 0 ? totalAll : undefined,
      };
    });

    // Add extra AC accounts that have post-recon commission but weren't in recon
    if (postReconData) {
      const reconAccountIds = new Set(compareData.issues.map(i => i.accountId));
      for (const extra of postReconData.extraAcAccounts) {
        if (extra.acReportCommission > 0 && !reconAccountIds.has(String(extra.acId))) {
          merged.push({
            type: 'ac_only',
            severity: 'info',
            accountId: String(extra.acId),
            username: extra.name,
            brand: extra.advertiser,
            partner: extra.advertiser,
            offer: '',
            geo: '',
            currency: 'EUR',
            details: `AC-only account (post-recon): ${fmtCurrency(extra.acReportCommission)}`,
            partnerGeoKey: `${extra.advertiser.toLowerCase().replace(/\s+/g, '')}|`,
            acPostReconCommission: extra.acReportCommission,
            acMatched: true,
            totalAllPeriods: extra.acReportCommission,
            missingInInvoice: true,
            missingInReconciliation: true,
            missingInPartner: true,
          });
        }
      }
    }

    return merged;
  }, [compareData, postReconData]);

  // -----------------------------------------------------------------------
  // Compute combined totals
  // -----------------------------------------------------------------------
  const combinedTotals = useMemo(() => {
    if (!compareData) return null;
    const reconTotals = compareData.totals;
    const totalACPostRecon = mergedIssues.reduce((s, i) => s + (i.acPostReconCommission || 0), 0);
    const totalACPostReconSynced = mergedIssues.reduce((s, i) => s + (i.acPostReconSynced || 0), 0);
    const totalAllPeriods = mergedIssues.reduce((s, i) => {
      const reconBest = Math.max(
        i.reconciliationEarnings || 0,
        i.partnerCommissionOurShare || 0,
        (i.adwstatsDataSynced !== false && i.adwstatsCommission) ? i.adwstatsCommission : 0,
        i.manualCheckAmount || 0,
      );
      return s + reconBest + (i.acPostReconCommission || 0);
    }, 0);

    return {
      ...reconTotals,
      acPostRecon: Math.round(totalACPostRecon * 100) / 100,
      acPostReconSynced: Math.round(totalACPostReconSynced * 100) / 100,
      totalAllPeriods: Math.round(totalAllPeriods * 100) / 100,
    };
  }, [compareData, mergedIssues]);

  // -----------------------------------------------------------------------
  // Filter issues
  // -----------------------------------------------------------------------
  const filteredIssues = useMemo(() => {
    let items = mergedIssues;

    switch (activeTab) {
      case 'critical': items = items.filter(i => i.severity === 'critical'); break;
      case 'underpaid': items = items.filter(i => i.type === 'underpayment'); break;
      case 'overpaid': items = items.filter(i => (i.revenueDifference || 0) > 1); break;
      case 'missing_invoice': items = items.filter(i => i.missingInInvoice && !i.missingInReconciliation); break;
      case 'missing_recon': items = items.filter(i => i.missingInReconciliation && i.type !== 'ac_only'); break;
      case 'missing_partner': items = items.filter(i => i.missingInPartner && !i.missingInReconciliation); break;
      case 'matches': items = items.filter(i => i.type === 'match'); break;
      case 'ac_only': items = items.filter(i => i.type === 'ac_only' || (i.acPostReconCommission && i.acPostReconCommission > 0)); break;
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(i =>
        i.accountId?.toLowerCase().includes(q) ||
        i.username?.toLowerCase().includes(q) ||
        i.brand?.toLowerCase().includes(q) ||
        i.partner?.toLowerCase().includes(q) ||
        i.offer?.toLowerCase().includes(q) ||
        i.geo?.toLowerCase().includes(q) ||
        i.details?.toLowerCase().includes(q) ||
        i.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }

    return items.sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  }, [mergedIssues, activeTab, searchTerm]);

  // Group filtered issues by partner+geo
  const filteredGroups = useMemo(() => {
    const groups = new Map<string, FinalGroup>();
    for (const iss of filteredIssues) {
      const key = iss.partnerGeoKey;
      if (!groups.has(key)) {
        groups.set(key, { partner: iss.partner, geo: iss.geo, issues: [], totalInvoiced: 0, shouldBe: 0, underpaid: 0, totalSynced: 0, totalACPostRecon: 0, totalAllPeriods: 0 });
      }
      const g = groups.get(key)!;
      g.issues.push(iss);
      g.totalInvoiced += iss.invoiceRevenue || 0;
      const syncedComm = (iss.adwstatsDataSynced !== false && iss.adwstatsCommission) ? iss.adwstatsCommission : 0;
      const reconBest = Math.max(iss.reconciliationEarnings || 0, iss.partnerCommissionOurShare || 0, syncedComm, iss.manualCheckAmount || 0);
      g.shouldBe += reconBest;
      if ((iss.revenueDifference || 0) < -1) g.underpaid += Math.abs(iss.revenueDifference || 0);
      if (iss.adwstatsAccountId && iss.adwstatsDataSynced !== false) g.totalSynced += iss.adwstatsCommission || 0;
      g.totalACPostRecon += iss.acPostReconCommission || 0;
      g.totalAllPeriods += reconBest + (iss.acPostReconCommission || 0);
    }
    return Array.from(groups.values()).sort((a, b) => b.totalAllPeriods - a.totalAllPeriods);
  }, [filteredIssues]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const acAccountsWithRevenue = mergedIssues.filter(i => i.acPostReconCommission && i.acPostReconCommission > 0).length;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Scale className="h-7 w-7 text-indigo-400" />
              Final Reconciliation
            </h1>
            <p className="text-zinc-400 mt-1 text-sm sm:text-base">
              Full period: Recon data (Mar 2023 – Nov 2024) + AC stats (Dec 2024+)
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isACConnected ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <Shield className="h-3.5 w-3.5" />
                AC Connected
              </span>
            ) : (
              <button
                onClick={() => setShowTokenInput(!showTokenInput)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-400 hover:text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg transition-colors"
              >
                <Key className="h-3.5 w-3.5" />
                Connect AC
              </button>
            )}
            <button
              onClick={handleImport}
              disabled={isImporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50"
            >
              {isImporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {isImporting ? 'Importing...' : 'Import from Sheets'}
            </button>
            <button
              onClick={fetchAllData}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {isACConnected && (
              <button
                onClick={handleDisconnect}
                className="text-xs text-zinc-500 hover:text-zinc-400 transition-colors"
              >
                Disconnect
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AC Token Input */}
      {showTokenInput && !isACConnected && (
        <div className="mb-6 bg-[hsl(225,15%,10%)] rounded-xl border border-amber-500/20 p-4">
          <p className="text-xs text-zinc-400 mb-3">Paste your AC Bearer token to fetch post-recon data from Affiliation Cloud</p>
          <div className="flex gap-2">
            <input
              type="password"
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              placeholder="Paste AC Bearer token..."
              className="flex-1 px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
            />
            <button
              onClick={handleSaveToken}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
            >
              Save Token
            </button>
            <button
              onClick={() => { setShowTokenInput(false); setTokenInput(''); }}
              className="px-3 py-2 text-sm text-zinc-400 hover:text-zinc-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && !compareData && (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading reconciliation data...</span>
          </div>
        </div>
      )}

      {/* Error / empty state */}
      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Data Available</h2>
          <p className="text-sm text-zinc-400 mb-6">{error}</p>
          <button
            onClick={handleImport}
            disabled={isImporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-500/50 text-white font-medium text-sm rounded-lg transition-colors"
          >
            {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isImporting ? 'Importing...' : 'Import from Google Sheets'}
          </button>
        </div>
      )}

      {/* Main content */}
      {compareData && combinedTotals && (
        <div className="space-y-6 animate-fade-in">
          {/* Period info bar */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-indigo-500/20 p-4">
            <div className="flex flex-wrap items-center gap-6 text-xs">
              <div>
                <span className="text-zinc-500">Recon period: </span>
                <span className="text-white font-medium">Mar 2023 – Nov 2024</span>
                <span className="text-zinc-600 ml-1">(Google Sheets)</span>
              </div>
              <div>
                <span className="text-zinc-500">Post-recon period: </span>
                <span className={isACConnected ? 'text-emerald-400 font-medium' : 'text-zinc-600'}>
                  {isACConnected && postReconData
                    ? postReconData.period.postRecon
                    : 'Dec 2024+ (connect AC to load)'}
                </span>
                {isACConnected && <span className="text-zinc-600 ml-1">(Affiliation Cloud)</span>}
              </div>
              {postReconData && (
                <div>
                  <span className="text-zinc-500">AC accounts matched: </span>
                  <span className="text-emerald-400 font-medium">{postReconData.summary.matched}</span>
                  <span className="text-zinc-600"> / {postReconData.summary.totalReconAccounts}</span>
                </div>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {combinedTotals.actualInvoiced > 0 && (
              <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-white/10 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <FileText className="h-3.5 w-3.5 text-white" />
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Actual Invoiced</p>
                </div>
                <p className="text-lg font-bold text-white tabular-nums">{fmtCurrency(combinedTotals.actualInvoiced)}</p>
                <p className="text-[10px] text-zinc-600">{combinedTotals.actualInvoiceCount} invoices</p>
              </div>
            )}
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className="h-3.5 w-3.5 text-zinc-400" />
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Commission Sheets</p>
              </div>
              <p className="text-lg font-bold text-zinc-300 tabular-nums">{fmtCurrency(combinedTotals.invoiced)}</p>
              <p className="text-[10px] text-zinc-600">Recon period only</p>
            </div>
            <SummaryCard label="Recon Best" value={fmtCurrency(combinedTotals.shouldBe)} icon={<Scale className="h-4 w-4" />} color="purple" />
            <SummaryCard label="Recon Earnings" value={fmtCurrency(combinedTotals.reconEarnings)} icon={<ArrowUpRight className="h-4 w-4" />} color="amber" />
            <SummaryCard label="Our Synced (AC)" value={fmtCurrency(combinedTotals.adwstatsSynced)} icon={<Database className="h-4 w-4" />} color="emerald" />
            {combinedTotals.acPostRecon > 0 && (
              <SummaryCard label="AC Post-Recon" value={fmtCurrency(combinedTotals.acPostRecon)} icon={<Shield className="h-4 w-4" />} color="indigo" />
            )}
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-indigo-500/30 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-indigo-400" />
                <p className="text-[10px] text-indigo-400 uppercase tracking-wider font-semibold">Total All Periods</p>
              </div>
              <p className="text-lg font-bold text-indigo-400 tabular-nums">{fmtCurrency(combinedTotals.totalAllPeriods)}</p>
              <p className="text-[10px] text-zinc-600">Recon + AC post-recon</p>
            </div>
          </div>

          {/* Corrections & Matching */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-4">
              <p className="text-xs text-zinc-400 mb-1">Corrections (+)</p>
              <p className="text-lg font-bold text-emerald-400 tabular-nums">
                +{fmtCurrency(combinedTotals.positiveCorrections)}
              </p>
            </div>
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-red-500/20 p-4">
              <p className="text-xs text-zinc-400 mb-1">Corrections (-)</p>
              <p className="text-lg font-bold text-red-400 tabular-nums">
                {fmtCurrency(combinedTotals.negativeCorrections)}
              </p>
            </div>
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
              <p className="text-xs text-zinc-400 mb-1">Adwstats Matching <span className="text-zinc-600">({compareData.adwstatsMatching.totalLocalAccounts} accounts)</span></p>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-sm text-emerald-400">
                  <Link2 className="h-3.5 w-3.5" />
                  {compareData.adwstatsMatching.matched} matched
                </span>
                <span className="flex items-center gap-1 text-sm text-amber-400">
                  <Unlink className="h-3.5 w-3.5" />
                  {compareData.adwstatsMatching.unmatched} unmatched
                </span>
                {compareData.adwstatsMatching.matchedNotSynced > 0 && (
                  <span className="flex items-center gap-1 text-sm text-amber-400/70">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {compareData.adwstatsMatching.matchedNotSynced} not synced
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Tabs + Search */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              <Filter className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              {([
                ['all', `All (${mergedIssues.length})`],
                ['critical', `Critical (${mergedIssues.filter(i => i.severity === 'critical').length})`],
                ['underpaid', `Underpaid (${mergedIssues.filter(i => i.type === 'underpayment').length})`],
                ['overpaid', `Overpaid (${mergedIssues.filter(i => (i.revenueDifference || 0) > 1).length})`],
                ['missing_invoice', `No Invoice (${mergedIssues.filter(i => i.missingInInvoice && !i.missingInReconciliation).length})`],
                ['missing_recon', `No Recon (${mergedIssues.filter(i => i.missingInReconciliation && i.type !== 'ac_only').length})`],
                ['missing_partner', `No Partner (${mergedIssues.filter(i => i.missingInPartner && !i.missingInReconciliation).length})`],
                ['matches', `Matches (${mergedIssues.filter(i => i.type === 'match').length})`],
                ...(acAccountsWithRevenue > 0 ? [['ac_only', `AC Post-Recon (${acAccountsWithRevenue})`]] : []),
              ] as [TabFilter, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                    activeTab === key
                      ? key === 'ac_only'
                        ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/30'
                        : 'bg-purple-500/10 text-purple-400 border border-purple-500/30'
                      : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search accounts..."
                className="pl-8 pr-3 py-1.5 w-56 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
              />
            </div>
          </div>

          {/* Grouped Comparison Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="p-4 border-b border-[hsl(225,15%,18%)]">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Scale className="h-4 w-4 text-indigo-400" />
                Final Comparison ({filteredIssues.length} accounts in {filteredGroups.length} groups)
              </h3>
            </div>

            {filteredGroups.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No results match your filters</div>
            ) : (
              <div className="divide-y divide-[hsl(225,15%,15%)]">
                {filteredGroups.map(group => {
                  const groupKey = `${group.partner}|${group.geo}`;
                  const isExpanded = expandedGroups.has(groupKey);

                  return (
                    <div key={groupKey}>
                      {/* Group header */}
                      <button
                        onClick={() => toggleGroup(groupKey)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[hsl(225,15%,12%)] transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                          <span className="text-sm font-medium text-white truncate">{group.partner || 'Unknown'}</span>
                          <span className="text-xs text-zinc-500">-</span>
                          <span className="text-xs text-indigo-400 font-medium">{group.geo || '—'}</span>
                          <span className="text-[10px] text-zinc-600">{group.issues.length} accounts</span>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Invoiced</span>
                            <span className="text-xs text-white tabular-nums">{fmtCurrency(group.totalInvoiced)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Recon Best</span>
                            <span className="text-xs text-amber-400 tabular-nums">{fmtCurrency(group.shouldBe)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Our Synced</span>
                            <span className="text-xs text-emerald-400 tabular-nums">{fmtCurrency(group.totalSynced)}</span>
                          </div>
                          {group.totalACPostRecon > 0 && (
                            <div className="text-right">
                              <span className="text-[10px] text-zinc-500 block">AC Post-Recon</span>
                              <span className="text-xs text-indigo-400 tabular-nums">{fmtCurrency(group.totalACPostRecon)}</span>
                            </div>
                          )}
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Total</span>
                            <span className="text-xs text-indigo-300 tabular-nums font-semibold">{fmtCurrency(group.totalAllPeriods)}</span>
                          </div>
                        </div>
                      </button>

                      {/* Group rows */}
                      {isExpanded && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-[hsl(225,15%,8%)]">
                              <tr>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Severity</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account ID</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Username</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Offer</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Invoice (EUR)</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Reconciliation</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Our Synced</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">AC Post-Recon</th>
                                <th className="text-right px-3 py-2 text-indigo-400 font-medium">Total</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Diff</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                              {group.issues.map((iss, idx) => {
                                const diff = iss.revenueDifference || 0;
                                const diffColor = Math.abs(diff) < 1 ? 'text-zinc-600' : diff < 0 ? 'text-red-400' : 'text-emerald-400';

                                return (
                                  <tr key={`${iss.accountId}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                                    <td className="px-3 py-2">
                                      <SeverityBadge severity={iss.severity} type={iss.type} />
                                    </td>
                                    <td className="px-3 py-2 text-zinc-300 tabular-nums">{iss.accountId || '—'}</td>
                                    <td className="px-3 py-2 text-white font-medium">{iss.username || (iss.type === 'no_accounts_tracked' && iss.brand ? <span className="text-amber-400/80 text-[10px]">{iss.brand}</span> : '—')}</td>
                                    <td className="px-3 py-2 text-zinc-400 max-w-[140px] truncate">{iss.offer || '—'}</td>
                                    <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">
                                      {iss.invoiceRevenue !== undefined ? fmtCurrency(iss.invoiceRevenue) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right text-amber-400 tabular-nums">
                                      {iss.reconciliationEarnings !== undefined ? fmtCurrency(iss.reconciliationEarnings) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      {iss.adwstatsAccountId ? (
                                        iss.adwstatsDataSynced === false ? (
                                          <span className="text-amber-400/70 text-[10px]">not synced</span>
                                        ) : (
                                          <span className="text-emerald-400">
                                            {fmtCurrency(iss.adwstatsCommission || 0)}
                                            {iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' && iss.adwstatsCommissionOriginal != null && (
                                              <span className="block text-[9px] text-emerald-400/50">
                                                {fmtOriginalCurrency(iss.adwstatsCommissionOriginal, iss.adwstatsCurrency)}
                                              </span>
                                            )}
                                          </span>
                                        )
                                      ) : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      {iss.acPostReconCommission ? (
                                        <span className="text-indigo-400">{fmtCurrency(iss.acPostReconCommission)}</span>
                                      ) : <span className="text-zinc-700">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-indigo-300">
                                      {iss.totalAllPeriods ? fmtCurrency(iss.totalAllPeriods) : '—'}
                                    </td>
                                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${diffColor}`}>
                                      {iss.revenueDifference !== undefined ? fmtCurrency(diff) : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                      {iss.adwstatsAccountName ? (
                                        <span className="flex items-center gap-1">
                                          <Link2 className={`h-3 w-3 ${iss.adwstatsDataSynced === false ? 'text-amber-500' : 'text-emerald-500'}`} />
                                          <span className="text-zinc-300 truncate max-w-[120px]" title={iss.adwstatsAccountName}>
                                            {iss.adwstatsAccountName}
                                          </span>
                                          {iss.adwstatsDataSynced === false ? (
                                            <span className="text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1 rounded">not synced</span>
                                          ) : (
                                            <span className="text-[9px] text-zinc-600">({iss.adwstatsMatchMethod})</span>
                                          )}
                                        </span>
                                      ) : iss.type === 'ac_only' ? (
                                        <span className="text-indigo-400/70 flex items-center gap-1">
                                          <Shield className="h-3 w-3" />
                                          <span className="text-[10px]">AC only</span>
                                        </span>
                                      ) : iss.accountId ? (
                                        <span className="text-zinc-600 flex items-center gap-1">
                                          <Unlink className="h-3 w-3" />
                                          <span className="text-[10px]">unmatched</span>
                                        </span>
                                      ) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Exchange Rates Info */}
          <div className="bg-[hsl(225,15%,8%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <p className="text-xs text-zinc-500">
              Exchange rates used (recon period averages): {Object.entries(compareData.exchangeRates).slice(0, 8).map(([cur, rate]) =>
                `1 ${cur} = ${Number(rate).toFixed(4)} EUR`
              ).join(', ')}
              {Object.keys(compareData.exchangeRates).length > 8 && ` and ${Object.keys(compareData.exchangeRates).length - 8} more`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
    zinc: 'text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,18%)]',
  };
  const c = colorMap[color] || colorMap.zinc;
  const parts = c.split(' ');
  return (
    <div className={`rounded-xl border p-3 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-base sm:text-lg font-bold tabular-nums ${parts[0]}`}>{value}</p>
    </div>
  );
};

const SeverityBadge: React.FC<{ severity: string; type?: string }> = ({ severity, type }) => {
  if (type === 'ac_only') {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium bg-indigo-500/10 text-indigo-400 border-indigo-500/20">
        AC Only
      </span>
    );
  }
  const config: Record<string, { label: string; cls: string }> = {
    critical: { label: 'Critical', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
    high: { label: 'High', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
    medium: { label: 'Medium', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
    low: { label: 'Low', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    info: { label: 'Info', cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  };
  const c = config[severity] || config.info;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
};

export default RaketechFinalRecon;
